// ============================================
// Storage Service - Multi-User LocalStorage
// ============================================

import {
  AppConfig,
  DEFAULT_CONFIG,
  UserProfile,
  UserPreferences,
  DEFAULT_NOTIFICATION_SETTINGS,
  DEFAULT_AUCTION_FILTERS,
  DomainCategory,
  DEFAULT_CATEGORIES,
  WatchlistItem,
  AlertEvent,
  WonDomain,
  BatchAnalysisJob,
} from "../types";

// ============================================
// Storage Keys
// ============================================

const STORAGE_KEYS = {
  userProfile: (username: string) => `zpd_${username}_profile`,
  userPrefs: (username: string) => `zpd_${username}_preferences`,
  userConfig: (username: string) => `zpd_${username}_config`,
  userAlerts: (username: string) => `zpd_${username}_alerts`,
  userPortfolio: (username: string) => `zpd_${username}_portfolio`,
  userBatchJobs: (username: string) => `zpd_${username}_batch_jobs`,
  usersList: "zpd_users_list",
  currentUser: "zpd_current_user",
  // Legacy key for migration
  legacyConfig: "legal_domain_finder_config",
};

// ============================================
// Storage Service Class
// ============================================

export class StorageService {
  // ==========================================
  // User Management
  // ==========================================

  /**
   * Get list of all registered usernames
   */
  listUsers(): string[] {
    const stored = localStorage.getItem(STORAGE_KEYS.usersList);
    if (!stored) return [];
    try {
      return JSON.parse(stored);
    } catch {
      return [];
    }
  }

  /**
   * Add a username to the users list
   */
  private addUserToList(username: string): void {
    const users = this.listUsers();
    if (!users.includes(username)) {
      users.push(username);
      localStorage.setItem(STORAGE_KEYS.usersList, JSON.stringify(users));
    }
  }

  /**
   * Get current logged-in username
   */
  getCurrentUser(): string | null {
    return localStorage.getItem(STORAGE_KEYS.currentUser);
  }

  /**
   * Set current logged-in username
   */
  setCurrentUser(username: string): void {
    localStorage.setItem(STORAGE_KEYS.currentUser, username);
    this.addUserToList(username);
  }

  /**
   * Clear current user (logout)
   */
  clearCurrentUser(): void {
    localStorage.removeItem(STORAGE_KEYS.currentUser);
  }

  // ==========================================
  // User Profile
  // ==========================================

  /**
   * Get user profile
   */
  getUserProfile(username: string): UserProfile | null {
    const stored = localStorage.getItem(STORAGE_KEYS.userProfile(username));
    if (!stored) return null;
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }

  /**
   * Save user profile
   */
  saveUserProfile(profile: UserProfile): void {
    localStorage.setItem(
      STORAGE_KEYS.userProfile(profile.username),
      JSON.stringify(profile)
    );
    this.addUserToList(profile.username);
  }

  /**
   * Create default profile for new user
   */
  createDefaultProfile(username: string): UserProfile {
    const profile: UserProfile = {
      username,
      createdAt: new Date().toISOString(),
      lastLogin: new Date().toISOString(),
      activeCategory: "law",
    };
    this.saveUserProfile(profile);
    return profile;
  }

  /**
   * Update last login time
   */
  updateLastLogin(username: string): void {
    const profile = this.getUserProfile(username);
    if (profile) {
      profile.lastLogin = new Date().toISOString();
      this.saveUserProfile(profile);
    }
  }

  // ==========================================
  // User Preferences
  // ==========================================

  /**
   * Get user preferences
   */
  getUserPreferences(username: string): UserPreferences {
    const stored = localStorage.getItem(STORAGE_KEYS.userPrefs(username));
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        // Return defaults on parse error
      }
    }
    return this.createDefaultPreferences();
  }

  /**
   * Save user preferences
   */
  saveUserPreferences(username: string, prefs: UserPreferences): void {
    localStorage.setItem(
      STORAGE_KEYS.userPrefs(username),
      JSON.stringify(prefs)
    );
  }

  /**
   * Create default preferences
   */
  createDefaultPreferences(): UserPreferences {
    return {
      defaultFilters: { ...DEFAULT_AUCTION_FILTERS },
      watchlistKeywords: [],
      notifications: { ...DEFAULT_NOTIFICATION_SETTINGS },
      categories: [...DEFAULT_CATEGORIES],
    };
  }

  /**
   * Update active category
   */
  setActiveCategory(username: string, categoryId: string): void {
    const profile = this.getUserProfile(username);
    if (profile) {
      profile.activeCategory = categoryId;
      this.saveUserProfile(profile);
    }
  }

  // ==========================================
  // User Config (API Keys)
  // ==========================================

  /**
   * Get user config (API credentials and settings)
   */
  getUserConfig(username: string): AppConfig {
    const stored = localStorage.getItem(STORAGE_KEYS.userConfig(username));
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        // Return defaults on parse error
      }
    }
    // Try to migrate from legacy config
    return this.migrateLegacyConfig(username);
  }

  /**
   * Save user config
   */
  saveUserConfig(username: string, config: AppConfig): void {
    localStorage.setItem(
      STORAGE_KEYS.userConfig(username),
      JSON.stringify(config)
    );
  }

  /**
   * Migrate legacy config (single user) to new multi-user format
   */
  private migrateLegacyConfig(username: string): AppConfig {
    const legacyStored = localStorage.getItem(STORAGE_KEYS.legacyConfig);
    if (legacyStored) {
      try {
        const legacyConfig = JSON.parse(legacyStored);
        // Save to new location
        this.saveUserConfig(username, legacyConfig);
        return legacyConfig;
      } catch {
        // Return defaults on parse error
      }
    }
    return { ...DEFAULT_CONFIG };
  }

  // ==========================================
  // Alerts
  // ==========================================

  /**
   * Get user alerts
   */
  getUserAlerts(username: string): AlertEvent[] {
    const stored = localStorage.getItem(STORAGE_KEYS.userAlerts(username));
    if (!stored) return [];
    try {
      return JSON.parse(stored);
    } catch {
      return [];
    }
  }

  /**
   * Save user alerts
   */
  saveUserAlerts(username: string, alerts: AlertEvent[]): void {
    // Keep only last 100 alerts
    const trimmed = alerts.slice(-100);
    localStorage.setItem(
      STORAGE_KEYS.userAlerts(username),
      JSON.stringify(trimmed)
    );
  }

  /**
   * Add a new alert
   */
  addAlert(username: string, alert: AlertEvent): void {
    const alerts = this.getUserAlerts(username);
    alerts.push(alert);
    this.saveUserAlerts(username, alerts);
  }

  /**
   * Mark alert as read
   */
  markAlertAsRead(username: string, alertId: string): void {
    const alerts = this.getUserAlerts(username);
    const alert = alerts.find((a) => a.id === alertId);
    if (alert) {
      alert.read = true;
      this.saveUserAlerts(username, alerts);
    }
  }

  /**
   * Mark all alerts as read
   */
  markAllAlertsAsRead(username: string): void {
    const alerts = this.getUserAlerts(username);
    alerts.forEach((a) => (a.read = true));
    this.saveUserAlerts(username, alerts);
  }

  /**
   * Get unread alert count
   */
  getUnreadAlertCount(username: string): number {
    const alerts = this.getUserAlerts(username);
    return alerts.filter((a) => !a.read).length;
  }

  /**
   * Clear all alerts
   */
  clearAlerts(username: string): void {
    localStorage.removeItem(STORAGE_KEYS.userAlerts(username));
  }

  // ==========================================
  // Portfolio (Won Domains)
  // ==========================================

  /**
   * Get user portfolio
   */
  getUserPortfolio(username: string): WonDomain[] {
    const stored = localStorage.getItem(STORAGE_KEYS.userPortfolio(username));
    if (!stored) return [];
    try {
      return JSON.parse(stored);
    } catch {
      return [];
    }
  }

  /**
   * Save user portfolio
   */
  saveUserPortfolio(username: string, portfolio: WonDomain[]): void {
    localStorage.setItem(
      STORAGE_KEYS.userPortfolio(username),
      JSON.stringify(portfolio)
    );
  }

  /**
   * Add domain to portfolio
   */
  addToPortfolio(username: string, domain: WonDomain): void {
    const portfolio = this.getUserPortfolio(username);
    // Check if domain already exists
    const existing = portfolio.find((d) => d.domain === domain.domain);
    if (!existing) {
      portfolio.push(domain);
      this.saveUserPortfolio(username, portfolio);
    }
  }

  /**
   * Update domain in portfolio
   */
  updatePortfolioDomain(username: string, domain: WonDomain): void {
    const portfolio = this.getUserPortfolio(username);
    const index = portfolio.findIndex((d) => d.id === domain.id);
    if (index !== -1) {
      portfolio[index] = domain;
      this.saveUserPortfolio(username, portfolio);
    }
  }

  /**
   * Remove domain from portfolio
   */
  removeFromPortfolio(username: string, domainId: string): void {
    const portfolio = this.getUserPortfolio(username);
    const filtered = portfolio.filter((d) => d.id !== domainId);
    this.saveUserPortfolio(username, filtered);
  }

  // ==========================================
  // Batch Analysis Jobs
  // ==========================================

  /**
   * Get user batch jobs
   */
  getUserBatchJobs(username: string): BatchAnalysisJob[] {
    const stored = localStorage.getItem(STORAGE_KEYS.userBatchJobs(username));
    if (!stored) return [];
    try {
      return JSON.parse(stored);
    } catch {
      return [];
    }
  }

  /**
   * Save user batch jobs
   */
  saveUserBatchJobs(username: string, jobs: BatchAnalysisJob[]): void {
    // Keep only last 20 jobs
    const trimmed = jobs.slice(-20);
    localStorage.setItem(
      STORAGE_KEYS.userBatchJobs(username),
      JSON.stringify(trimmed)
    );
  }

  /**
   * Add or update a batch job
   */
  saveBatchJob(username: string, job: BatchAnalysisJob): void {
    const jobs = this.getUserBatchJobs(username);
    const index = jobs.findIndex((j) => j.id === job.id);
    if (index !== -1) {
      jobs[index] = job;
    } else {
      jobs.push(job);
    }
    this.saveUserBatchJobs(username, jobs);
  }

  /**
   * Get a specific batch job
   */
  getBatchJob(username: string, jobId: string): BatchAnalysisJob | null {
    const jobs = this.getUserBatchJobs(username);
    return jobs.find((j) => j.id === jobId) || null;
  }

  // ==========================================
  // Watchlist
  // ==========================================

  /**
   * Get watchlist from user preferences
   */
  getWatchlist(username: string): WatchlistItem[] {
    const prefs = this.getUserPreferences(username);
    return prefs.watchlistKeywords;
  }

  /**
   * Save watchlist to user preferences
   */
  saveWatchlist(username: string, watchlist: WatchlistItem[]): void {
    const prefs = this.getUserPreferences(username);
    prefs.watchlistKeywords = watchlist;
    this.saveUserPreferences(username, prefs);
  }

  /**
   * Add item to watchlist
   */
  addToWatchlist(username: string, item: WatchlistItem): void {
    const watchlist = this.getWatchlist(username);
    // Check if keyword already exists
    const existing = watchlist.find(
      (w) => w.keyword.toLowerCase() === item.keyword.toLowerCase()
    );
    if (!existing) {
      watchlist.push(item);
      this.saveWatchlist(username, watchlist);
    }
  }

  /**
   * Update watchlist item
   */
  updateWatchlistItem(username: string, item: WatchlistItem): void {
    const watchlist = this.getWatchlist(username);
    const index = watchlist.findIndex((w) => w.id === item.id);
    if (index !== -1) {
      watchlist[index] = item;
      this.saveWatchlist(username, watchlist);
    }
  }

  /**
   * Remove from watchlist
   */
  removeFromWatchlist(username: string, itemId: string): void {
    const watchlist = this.getWatchlist(username);
    const filtered = watchlist.filter((w) => w.id !== itemId);
    this.saveWatchlist(username, filtered);
  }

  /**
   * Increment match count for watchlist item
   */
  incrementWatchlistMatch(username: string, itemId: string): void {
    const watchlist = this.getWatchlist(username);
    const item = watchlist.find((w) => w.id === itemId);
    if (item) {
      item.matchCount += 1;
      item.lastMatch = new Date().toISOString();
      this.saveWatchlist(username, watchlist);
    }
  }

  // ==========================================
  // Categories
  // ==========================================

  /**
   * Get categories from user preferences
   */
  getCategories(username: string): DomainCategory[] {
    const prefs = this.getUserPreferences(username);
    return prefs.categories;
  }

  /**
   * Save categories to user preferences
   */
  saveCategories(username: string, categories: DomainCategory[]): void {
    const prefs = this.getUserPreferences(username);
    prefs.categories = categories;
    this.saveUserPreferences(username, prefs);
  }

  /**
   * Add a category
   */
  addCategory(username: string, category: DomainCategory): void {
    const categories = this.getCategories(username);
    const existing = categories.find((c) => c.id === category.id);
    if (!existing) {
      categories.push(category);
      this.saveCategories(username, categories);
    }
  }

  /**
   * Update a category
   */
  updateCategory(username: string, category: DomainCategory): void {
    const categories = this.getCategories(username);
    const index = categories.findIndex((c) => c.id === category.id);
    if (index !== -1) {
      categories[index] = category;
      this.saveCategories(username, categories);
    }
  }

  /**
   * Delete a category
   */
  deleteCategory(username: string, categoryId: string): void {
    const categories = this.getCategories(username);
    const filtered = categories.filter((c) => c.id !== categoryId);
    this.saveCategories(username, filtered);
  }

  /**
   * Get active category
   */
  getActiveCategory(username: string): DomainCategory | null {
    const profile = this.getUserProfile(username);
    if (!profile) return null;
    const categories = this.getCategories(username);
    return categories.find((c) => c.id === profile.activeCategory) || null;
  }

  // ==========================================
  // Utility Methods
  // ==========================================

  /**
   * Export all user data as JSON
   */
  exportUserData(username: string): string {
    const data = {
      profile: this.getUserProfile(username),
      preferences: this.getUserPreferences(username),
      config: this.getUserConfig(username),
      alerts: this.getUserAlerts(username),
      portfolio: this.getUserPortfolio(username),
      batchJobs: this.getUserBatchJobs(username),
    };
    return JSON.stringify(data, null, 2);
  }

  /**
   * Import user data from JSON
   */
  importUserData(username: string, jsonData: string): boolean {
    try {
      const data = JSON.parse(jsonData);
      if (data.profile) this.saveUserProfile(data.profile);
      if (data.preferences) this.saveUserPreferences(username, data.preferences);
      if (data.config) this.saveUserConfig(username, data.config);
      if (data.alerts) this.saveUserAlerts(username, data.alerts);
      if (data.portfolio) this.saveUserPortfolio(username, data.portfolio);
      if (data.batchJobs) this.saveUserBatchJobs(username, data.batchJobs);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Delete all user data
   */
  deleteUserData(username: string): void {
    localStorage.removeItem(STORAGE_KEYS.userProfile(username));
    localStorage.removeItem(STORAGE_KEYS.userPrefs(username));
    localStorage.removeItem(STORAGE_KEYS.userConfig(username));
    localStorage.removeItem(STORAGE_KEYS.userAlerts(username));
    localStorage.removeItem(STORAGE_KEYS.userPortfolio(username));
    localStorage.removeItem(STORAGE_KEYS.userBatchJobs(username));

    // Remove from users list
    const users = this.listUsers();
    const filtered = users.filter((u) => u !== username);
    localStorage.setItem(STORAGE_KEYS.usersList, JSON.stringify(filtered));
  }

  /**
   * Get storage usage for a user (approximate bytes)
   */
  getStorageUsage(username: string): number {
    let total = 0;
    const keys = [
      STORAGE_KEYS.userProfile(username),
      STORAGE_KEYS.userPrefs(username),
      STORAGE_KEYS.userConfig(username),
      STORAGE_KEYS.userAlerts(username),
      STORAGE_KEYS.userPortfolio(username),
      STORAGE_KEYS.userBatchJobs(username),
    ];

    for (const key of keys) {
      const value = localStorage.getItem(key);
      if (value) {
        total += key.length + value.length;
      }
    }

    return total * 2; // UTF-16 uses 2 bytes per character
  }
}

// Export singleton instance
export const storageService = new StorageService();
